/**
 * The check of the room before a voice sample is counted (ADR-0062): for about two seconds after
 * the microphone opens nobody speaks, and what the microphone hears is the room. Its noise is the
 * middle of the levels heard — a door or a word in those two seconds moves it little — read as
 * the true level in dB under full scale, and called quiet, noisy or too loud by the thresholds in
 * `voice-rules.ts` (provisional). The same two seconds give the speech meter the room it starts
 * from, on the meter's own scale, so the first word is not taken for the room.
 *
 * A meter that reads nothing — it is still waking up, or it could not be opened — says nothing of
 * the room: frames of digital silence are left out, and a check that heard only those cannot tell.
 * Pure.
 */
import { roomOf, VOICE_ROOM_CHECK_MS } from '@/components/humint/voice/voice-rules'
import type { VoiceRoom } from '@/components/humint/voice/voice-types'
import { SILENCE_DBFS } from '../recording/level-meter'

/** One reading of the microphone during the check, and the time since the one before it. */
export interface RoomSample {
  /** 0..1 on the recorder's meter scale. */
  level: number
  /** The true level, dB under full scale; null where the browser cannot read it. */
  dbfs: number | null
  dtMs: number
}

/** The check between two readings. */
export interface RoomCheck {
  elapsedMs: number
  /** The true level of every frame that held a sound. */
  dbfs: readonly number[]
  /** The meter's level of every frame that held a sound. */
  levels: readonly number[]
}

export const NEW_ROOM_CHECK: RoomCheck = { elapsedMs: 0, dbfs: [], levels: [] }

/** The check after one more reading. */
export function advanceRoomCheck(check: RoomCheck, sample: RoomSample): RoomCheck {
  if (sample.dtMs <= 0) return check
  const elapsedMs = check.elapsedMs + sample.dtMs
  const heard = sample.dbfs !== null && sample.dbfs > SILENCE_DBFS
  return {
    elapsedMs,
    dbfs: heard ? [...check.dbfs, sample.dbfs as number] : check.dbfs,
    levels: sample.level > 0 ? [...check.levels, sample.level] : check.levels,
  }
}

/** Whether the room has been listened to for long enough. */
export const isRoomChecked = (
  check: RoomCheck,
  durationMs: number = VOICE_ROOM_CHECK_MS
): boolean => check.elapsedMs >= durationMs

/** How far the check has come, 0..1. */
export function roomCheckShare(check: RoomCheck, durationMs: number = VOICE_ROOM_CHECK_MS): number {
  if (durationMs <= 0) return 1
  return Math.min(1, Math.max(0, check.elapsedMs / durationMs))
}

/** The middle of the values: half of them are under it. Null for none. */
function median(values: readonly number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

/** What the check found. */
export interface RoomReading {
  /** What the room is called; null where the check could not tell. */
  room: VoiceRoom | null
  /** The room's noise, dB under full scale; null where no true level was read. */
  noiseDbfs: number | null
  /** The room on the meter's own scale, 0..1: where the speech meter's floor starts. Null for none. */
  floor: number | null
}

export function readRoomCheck(check: RoomCheck): RoomReading {
  const noiseDbfs = median(check.dbfs)
  return {
    room: noiseDbfs === null ? null : roomOf(noiseDbfs),
    noiseDbfs,
    floor: median(check.levels),
  }
}
